import { useEffect } from "react";
import { X, ArrowRight, Server, AlertTriangle, FlaskConical } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function MultiVmLabRequestModal({
  open,
  onClose,
  cve,
  setCve,
  theme,
  setTheme,
  isSubmitting,
  onSubmit,
}) {
  const { i18n } = useTranslation();
  const isRtl = i18n.language === "ar";

  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  if (!open) return null;

  const canSubmit = theme.trim().length > 0 && !isSubmitting;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      dir={isRtl ? "rtl" : "ltr"}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative z-10 w-full max-w-2xl overflow-hidden rounded-md border border-(--color-primary)/30 bg-(--color-bg-card) shadow-[0_0_40px_rgba(0,0,0,0.6)]">
        {/* Decorative glow */}
        <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-md bg-(--color-primary)/10 blur-3xl" />

        {/* Header */}
        <div className="relative flex items-start justify-between border-b border-(--color-border) px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-md border border-(--color-primary)/30 bg-(--color-primary)/10">
              <Server className="h-5 w-5 text-(--color-primary)" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <div className="inline-flex items-center gap-2 rounded-md border border-(--color-primary)/30 bg-transparent px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-(--color-primary)">
                  Multi-VM Lab
                </div>
                <span className="inline-flex items-center gap-1 rounded-md border border-purple-400/40 bg-purple-400/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-purple-300">
                  <FlaskConical className="h-3 w-3" />
                  Beta
                </span>
              </div>
              <h2 className="mt-1 text-lg font-bold text-(--color-text-primary)">
                Request a Multi-VM Lab
              </h2>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="cursor-pointer rounded-lg p-1.5 text-(--color-text-muted) transition-colors hover:bg-white/5 hover:text-(--color-primary)"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="relative space-y-5 px-6 py-6">
          <p className="text-sm leading-relaxed text-(--color-text-muted) dark:text-gray-400">
            Describe a vulnerability theme and optionally provide a CVE. The AI
            engine will build a multi-VM lab with network isolation and pivot
            scenarios.
          </p>

          <div className="relative flex items-start gap-3 overflow-hidden rounded-md border border-red-500/25 bg-red-500/[0.07] px-4 py-3">
            <span className="pointer-events-none absolute inset-y-0 left-0 w-0.5 bg-red-500/70" />
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-400" />
            <div className="space-y-1">
              <p className="text-[11px] font-bold uppercase tracking-wider text-red-400">
                Disclaimer · Early Access
              </p>
              <p className="text-[12.5px] leading-relaxed text-red-300/90">
                This is an experimental feature. You may encounter bugs, latency
                or unexpected behavior. The capability has been opened for
                assessment and feedback purposes only. Testing and updates are
                being pushed actively, which may temporarily disrupt
                functionality.
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold uppercase tracking-wider text-(--color-text-muted)">
                CVE (optional)
              </label>
              <input
                type="text"
                placeholder="e.g. CVE-2024-1086"
                value={cve}
                onChange={(e) => setCve(e.target.value)}
                className="w-full rounded-md border border-(--color-border) bg-(--color-bg-card) px-4 py-2.5 text-sm text-(--color-text-primary) placeholder-(--color-text-muted) transition focus:border-(--color-primary) focus:outline-none focus:ring-2 focus:ring-(--color-primary)/30 dark:bg-black"
              />
            </div>

            <div className="space-y-1.5">
              <label className="block text-xs font-semibold uppercase tracking-wider text-(--color-text-muted)">
                Vulnerability theme <span className="text-red-500">*</span>
              </label>
              <textarea
                rows={3}
                placeholder="Describe the vulnerability theme you want to practice..."
                value={theme}
                onChange={(e) => setTheme(e.target.value)}
                className="w-full resize-none rounded-md border border-(--color-border) bg-(--color-bg-card) px-4 py-2.5 text-sm text-(--color-text-primary) placeholder-(--color-text-muted) transition focus:border-(--color-primary) focus:outline-none focus:ring-2 focus:ring-(--color-primary)/30 dark:bg-black"
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="relative flex items-center justify-end gap-3 border-t border-(--color-border) px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="cursor-pointer rounded-md border border-(--color-border) bg-transparent px-5 py-2.5 text-sm font-semibold text-(--color-text-muted) transition hover:bg-white/5 hover:text-(--color-text-primary)"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!canSubmit}
            onClick={onSubmit}
            className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-md border border-(--color-primary) bg-(--color-primary) px-6 py-2.5 text-sm font-semibold text-(--color-text-on-primary) shadow-[0_0_20px_rgba(0,200,80,0.25)] transition hover:-translate-y-px hover:opacity-95 hover:shadow-[0_0_28px_rgba(0,200,80,0.4)] disabled:cursor-not-allowed disabled:opacity-40"
          >
            {isSubmitting ? "Submitting..." : "Request Lab"}
            {!isSubmitting && <ArrowRight className="h-4 w-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}
